import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import api from "../lib/api";
import { photoUrl } from "../lib/photo";
import { Phone, PhoneOff, Video as VideoIcon, Mic, MicOff, VideoOff, PhoneIncoming } from "lucide-react";
import { toast } from "sonner";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

const wsUrl = () => `${(process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")}/api/ws/presence`;

export function CallProvider({ children }) {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [callState, setCallState] = useState("idle"); // idle | outgoing | incoming | connecting | active
  const [peer, setPeer] = useState(null); // {user_id, name, photo}
  const [withVideo, setWithVideo] = useState(false);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const pendingIce = useRef([]);
  const incomingRef = useRef(null); // {from, sdp, video, name, photo}
  const stateRef = useRef("idle");
  const setState = (s) => { stateRef.current = s; setCallState(s); };

  const send = (obj) => { try { wsRef.current?.readyState === 1 && wsRef.current.send(JSON.stringify(obj)); } catch { /* noop */ } };

  const cleanup = useCallback(() => {
    try { pcRef.current?.getSenders().forEach((s) => s.track?.stop()); } catch { /* noop */ }
    try { pcRef.current?.close(); } catch { /* noop */ }
    localRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current = null; localRef.current = null; pendingIce.current = []; incomingRef.current = null;
    setLocalStream(null); setRemoteStream(null); setMuted(false); setCamOff(false);
    setState("idle"); setPeer(null);
  }, []);

  const buildPc = useCallback(async (targetId) => {
    const { data } = await api.get("/webrtc/ice-servers");
    const pc = new RTCPeerConnection({ iceServers: data.ice_servers });
    pc.onicecandidate = (e) => { if (e.candidate) send({ type: "call:ice", to: targetId, candidate: e.candidate }); };
    pc.ontrack = (e) => setRemoteStream(e.streams[0]);
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") setState("active");
      if (st === "failed" || st === "disconnected" || st === "closed") { if (stateRef.current !== "idle") { toast("Call ended"); cleanup(); } }
    };
    pcRef.current = pc;
    return pc;
  }, [cleanup]);

  const getMedia = async (video) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video });
    localRef.current = stream; setLocalStream(stream);
    return stream;
  };

  const startCall = useCallback(async (peerId, peerProfile, video) => {
    if (stateRef.current !== "idle") { toast.error("You're already on a call."); return; }
    if (!onlineUsers.has(peerId)) { toast.error(`${peerProfile?.first_name || "They"} are offline right now.`); return; }
    try {
      setPeer({ user_id: peerId, name: peerProfile?.first_name || "Member", photo: (typeof peerProfile?.photos?.[0] === "string" ? peerProfile.photos[0] : peerProfile?.photos?.[0]?.url) || (typeof peerProfile?.photo === "string" ? peerProfile.photo : peerProfile?.photo?.url) });
      setWithVideo(video); setState("outgoing");
      const stream = await getMedia(video);
      const pc = await buildPc(peerId);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "call:invite", to: peerId, video, name: user?.profile?.first_name || "A member", sdp: offer });
    } catch (e) {
      // Notify the callee so their incoming overlay tears down if the invite was already sent
      send({ type: "call:end", to: peerId });
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        toast.error(`Allow ${video ? "camera and microphone" : "microphone"} access to call.`);
      } else if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
        toast.error(`No ${video ? "camera/microphone" : "microphone"} found on this device.`);
      } else {
        toast.error("Couldn't start the call. Please try again.");
      }
      cleanup();
    }
  }, [onlineUsers, buildPc, cleanup, user]);

  const acceptCall = useCallback(async () => {
    const inc = incomingRef.current;
    if (!inc) return;
    try {
      setState("connecting");
      const stream = await getMedia(inc.video);
      const pc = await buildPc(inc.from);
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(inc.sdp));
      for (const c of pendingIce.current) { try { await pc.addIceCandidate(c); } catch { /* noop */ } }
      pendingIce.current = [];
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "call:answer", to: inc.from, sdp: answer });
    } catch (e) {
      toast.error("Couldn't join the call. Check camera/mic permissions.");
      send({ type: "call:decline", to: inc.from }); cleanup();
    }
  }, [buildPc, cleanup]);

  const declineCall = useCallback(() => {
    const inc = incomingRef.current;
    if (inc) send({ type: "call:decline", to: inc.from });
    cleanup();
  }, [cleanup]);

  const hangUp = useCallback(() => {
    if (peer) send({ type: "call:end", to: peer.user_id });
    cleanup();
  }, [peer, cleanup]);

  const toggleMute = () => { const s = localRef.current; if (!s) return; const a = s.getAudioTracks()[0]; if (a) { a.enabled = !a.enabled; setMuted(!a.enabled); } };
  const toggleCam = () => { const s = localRef.current; if (!s) return; const v = s.getVideoTracks()[0]; if (v) { v.enabled = !v.enabled; setCamOff(!v.enabled); } };

  const onSignal = useCallback(async (msg) => {
    const t = msg.type;
    if (t === "presence") {
      setOnlineUsers((prev) => { const n = new Set(prev); msg.online ? n.add(msg.user_id) : n.delete(msg.user_id); return n; });
      return;
    }
    if (t === "call:invite") {
      if (stateRef.current !== "idle") { send({ type: "call:decline", to: msg.from }); return; }
      incomingRef.current = { from: msg.from, sdp: msg.sdp, video: msg.video, name: msg.name };
      setPeer({ user_id: msg.from, name: msg.name || "Member" });
      setWithVideo(msg.video); setState("incoming");
      return;
    }
    if (t === "call:answer") {
      try { await pcRef.current?.setRemoteDescription(new RTCSessionDescription(msg.sdp)); setState("active"); } catch { /* noop */ }
      return;
    }
    if (t === "call:ice") {
      const cand = msg.candidate;
      if (pcRef.current?.remoteDescription) { try { await pcRef.current.addIceCandidate(cand); } catch { /* noop */ } }
      else pendingIce.current.push(cand);
      return;
    }
    if (t === "call:decline") { if (stateRef.current !== "idle") toast(`${peer?.name || "They"} declined the call`); cleanup(); return; }
    if (t === "call:end") { if (stateRef.current !== "idle") toast("Call ended"); cleanup(); return; }
    if (t === "call:unavailable") { toast.error("They're not available right now."); cleanup(); return; }
    if (t === "call:error") { toast.error(msg.detail || "Call failed."); cleanup(); return; }
  }, [cleanup, peer]);

  // Keep the latest onSignal in a ref so the presence socket doesn't reconnect on every re-render
  const onSignalRef = useRef(onSignal);
  useEffect(() => { onSignalRef.current = onSignal; }, [onSignal]);

  // Presence WebSocket lifecycle — only (re)connects when the logged-in user changes
  const userId = user?.user_id;
  useEffect(() => {
    if (!userId) return undefined;
    let closed = false; let retry;
    const connect = () => {
      let ws;
      try { ws = new WebSocket(wsUrl()); } catch { return; }
      wsRef.current = ws;
      ws.onmessage = (e) => { try { onSignalRef.current(JSON.parse(e.data)); } catch (err) { console.debug("presence msg parse failed", err); } };
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 3000); };
    };
    connect();
    api.get("/online").then(({ data }) => setOnlineUsers(new Set((data.online || []).map((o) => o.user_id)))).catch(() => {});
    return () => { closed = true; clearTimeout(retry); try { wsRef.current?.close(); } catch { /* socket not open */ } cleanup(); };
  }, [userId, cleanup]);

  return (
    <CallCtx.Provider value={{ onlineUsers, isOnline: (id) => onlineUsers.has(id), startCall, callState, peer }}>
      {children}
      {callState !== "idle" && (
        <CallOverlay {...{ callState, peer, withVideo, muted, camOff, localStream, remoteStream, acceptCall, declineCall, hangUp, toggleMute, toggleCam }} />
      )}
    </CallCtx.Provider>
  );
}

function Stream({ stream, muted, className }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
}

function CallOverlay({ callState, peer, withVideo, muted, camOff, localStream, remoteStream, acceptCall, declineCall, hangUp, toggleMute, toggleCam }) {
  const avatar = peer?.photo ? photoUrl(peer.photo) : null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="call-overlay">
      <div className="relative w-full max-w-2xl">
        {withVideo && (callState === "active" || callState === "connecting") && (
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video mb-5">
            <Stream stream={remoteStream} className="absolute inset-0 w-full h-full object-cover" />
            <Stream stream={localStream} muted className="absolute bottom-3 right-3 w-32 h-24 object-cover rounded-lg border border-white/20" />
          </div>
        )}
        {!withVideo && <Stream stream={remoteStream} className="hidden" />}

        <div className="text-center">
          {(!withVideo || callState !== "active") && (
            <div className="h-24 w-24 rounded-full mx-auto mb-4 overflow-hidden bg-white/10 flex items-center justify-center">
              {avatar ? <img src={avatar} alt={peer?.name} className="h-full w-full object-cover" /> : <span className="font-display text-3xl text-primary">{peer?.name?.[0] || "?"}</span>}
            </div>
          )}
          <h3 className="font-display text-2xl text-white" data-testid="call-peer-name">{peer?.name}</h3>
          <p className="text-white/60 text-sm mb-6">
            {callState === "incoming" && `Incoming ${withVideo ? "video" : "voice"} call…`}
            {callState === "outgoing" && "Calling…"}
            {callState === "connecting" && "Connecting…"}
            {callState === "active" && (withVideo ? "" : "In call")}
          </p>

          <div className="flex items-center justify-center gap-4">
            {callState === "incoming" ? (
              <>
                <button onClick={declineCall} data-testid="call-decline" className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white"><PhoneOff size={22} /></button>
                <button onClick={acceptCall} data-testid="call-accept" className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-500 flex items-center justify-center text-white animate-pulse"><PhoneIncoming size={22} /></button>
              </>
            ) : (
              <>
                {(callState === "active" || callState === "connecting") && (
                  <button onClick={toggleMute} data-testid="call-mute" className={`h-12 w-12 rounded-full flex items-center justify-center ${muted ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"}`}>{muted ? <MicOff size={20} /> : <Mic size={20} />}</button>
                )}
                {withVideo && (callState === "active" || callState === "connecting") && (
                  <button onClick={toggleCam} data-testid="call-camera" className={`h-12 w-12 rounded-full flex items-center justify-center ${camOff ? "bg-white text-black" : "bg-white/15 text-white hover:bg-white/25"}`}>{camOff ? <VideoOff size={20} /> : <VideoIcon size={20} />}</button>
                )}
                <button onClick={hangUp} data-testid="call-hangup" className="h-14 w-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white"><PhoneOff size={22} /></button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
